/**
 * @file: index.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * The originating-visit summary ("上次到店服务信息"): visit metadata plus the
 * serviced items / faults and a link out to the full service record.
 */

import { useAppNavigation } from '@euler/app/components/AppNavigationProvider';
import { LeadLastServiceInfo } from '@euler/model/lead/types';
import { useCallback } from 'react';
import { Card } from '../common/Card';
import { SectionHeader } from '../common/SectionHeader';
import { SectionLink } from '../common/SectionLink';
import { Field, formatDate } from '../leadDetailKit';
import { ItemChip } from './ItemChip';
import { WrapRow } from './WrapRow';

export const LastServiceCard = ({ info }: { info: LeadLastServiceInfo }) => {
  const navigation = useAppNavigation();
  const onOpenRecord = useCallback(() => {
    if (info.serviceRecordId) {
      navigation.navigate('ServiceRecord', { id: info.serviceRecordId });
    } else if (info.taskNo) {
      navigation.navigate('Task', { taskNo: info.taskNo });
    }
  }, [navigation, info.serviceRecordId, info.taskNo]);

  const tags = (info.tags ?? []).filter(Boolean);
  const serviceItems = [
    ...info.items.map(i => ({ name: i.name, solved: true })),
    ...info.faultItems.map(f => ({ name: f.name, solved: f.solved })),
  ].filter(i => !!i.name);

  return (
    <>
      <SectionHeader
        title="上次到店服务信息"
        right={
          info.serviceRecordId || info.taskNo ? (
            <SectionLink label="查看记录" onPress={onOpenRecord} />
          ) : null
        }
      />
      <Card>
        <Field
          label="进店日期"
          value={formatDate(info.orderedAt)}
          divider={false}
        />
        {tags.length ? (
          <WrapRow label="业务类型">
            {tags.map(tag => (
              <ItemChip key={tag} name={tag} solved />
            ))}
          </WrapRow>
        ) : null}
        <Field label="服务顾问" value={info.serviceAgentName} />
        <Field
          label="进店里程"
          value={info.mileage ? `${info.mileage} km` : null}
        />
        <Field label="待处理项" value={`${info.pendingIssueCount} 项`} />
        {serviceItems.length ? (
          <WrapRow label="服务项目">
            {serviceItems.map((item, index) => (
              <ItemChip
                key={`${item.name}-${index}`}
                name={item.name}
                solved={item.solved}
              />
            ))}
          </WrapRow>
        ) : null}
      </Card>
    </>
  );
};
